import React, { useRef, useEffect, useState } from 'react';
import { X, Map, MapPin } from 'lucide-react';

interface MapAdjustmentModalProps {
  isOpen: boolean;
  initialLocation: { lat: number; lng: number };
  currentGPSLocation: { lat: number; lng: number };
  onClose: () => void;
  onConfirm: (location: { lat: number; lng: number }) => void;
}

export const MapAdjustmentModal: React.FC<MapAdjustmentModalProps> = ({
  isOpen,
  initialLocation,
  currentGPSLocation,
  onClose,
  onConfirm
}) => {
  const [tempLocation, setTempLocation] = useState({ lat: 0, lng: 0 });
  const [mapLoaded, setMapLoaded] = useState(false);
  
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const currentLocationMarkerRef = useRef<any>(null);

  useEffect(() => {
    if (isOpen && initialLocation.lat !== 0 && initialLocation.lng !== 0) {
      setTempLocation({
        lat: initialLocation.lat,
        lng: initialLocation.lng
      });
      setMapLoaded(false);
      setTimeout(() => {
        initializeMap(initialLocation.lat, initialLocation.lng);
      }, 100);
    }
  }, [isOpen, initialLocation]);

  const loadGoogleMaps = () => {
    return new Promise((resolve, reject) => {
      // Check if Google Maps is already loaded
      if ((window as any).google && (window as any).google.maps) {
        resolve(true);
        return;
      }
      
      // Check if script is already loading
      if ((window as any).googleMapsLoading) {
        const checkInterval = setInterval(() => {
          if ((window as any).google && (window as any).google.maps) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 100);
        return;
      }
      
      // Mark as loading
      (window as any).googleMapsLoading = true;
      
      // Create script element
      const script = document.createElement('script');
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        reject(new Error('Google Maps API key not found'));
        return;
      }
      
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        (window as any).googleMapsLoading = false;
        resolve(true);
      };
      
      script.onerror = () => {
        (window as any).googleMapsLoading = false;
        reject(new Error('Failed to load Google Maps API'));
      };
      
      document.head.appendChild(script);
    });
  };

  const initializeMap = async (outletLat?: number, outletLng?: number) => {
    if (!mapRef.current) return;
    
    // Use provided coordinates or fall back to tempLocation
    const centerLat = outletLat || tempLocation.lat;
    const centerLng = outletLng || tempLocation.lng;
    
    // Original GPS location (for blue marker)
    const originalLatitude = currentGPSLocation.lat;
    const originalLongitude = currentGPSLocation.lng;
    
    if (centerLat === 0 || centerLng === 0) {
      console.warn("Coordinates not set yet — not initializing map.");
      return;
    }
    
    try {
      // Ensure Google Maps API is fully loaded
      await loadGoogleMaps();
      
      const { google } = window as any;
      
      // Double check that all required Google Maps components are available
      if (!google || !google.maps || !google.maps.Map || !google.maps.Marker) {
        console.log('Google Maps not fully loaded, retrying...');
        // Wait a bit more and try again
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (!google || !google.maps || !google.maps.Map || !google.maps.Marker) {
          console.error('Google Maps API not fully loaded after retry');
          return;
        }
      }
      
      const mapOptions = {
        zoom: 16,
        center: { lat: centerLat, lng: centerLng },
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        gestureHandling: 'cooperative'
      };

      const map = new google.maps.Map(mapRef.current, mapOptions);
      mapInstanceRef.current = map;

      // Wait for map to be fully initialized
      google.maps.event.addListenerOnce(map, 'idle', () => {
        // Add blue circle for original GPS location (user's current position) - only if we have original coordinates
        if (originalLatitude !== 0 && originalLongitude !== 0) {
          const currentLocationMarker = new google.maps.Marker({
            position: { lat: originalLatitude, lng: originalLongitude },
            map: map,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#4285F4',
              fillOpacity: 0.8,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            },
            title: 'Your Current Location (GPS)',
            draggable: false
          });
          
          currentLocationMarkerRef.current = currentLocationMarker;
        }
        
        // Add red marker for outlet location (draggable)
        const outletMarker = new google.maps.Marker({
          position: { lat: centerLat, lng: centerLng },
          map: map,
          draggable: true,
          title: 'Outlet Location (Drag to adjust)',
          icon: {
            path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
            scale: 6,
            fillColor: '#EA4335',
            fillOpacity: 0.8,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
          }
        });
        
        markerRef.current = outletMarker;
        
        // Auto-confirm when marker is dragged
        outletMarker.addListener('dragend', () => {
          const position = outletMarker.getPosition();
          if (position) {
            const newLocation = {
              lat: position.lat(),
              lng: position.lng()
            };
            setTempLocation(newLocation);
            
            // Auto-confirm the new location
            setTimeout(() => {
              onConfirm(newLocation);
            }, 300); // Small delay for visual feedback
          }
        });

        // Auto-confirm when map is clicked  
        map.addListener('click', (event: any) => {
          const clickedLocation = event.latLng;
          const newLocation = {
            lat: clickedLocation.lat(),
            lng: clickedLocation.lng()
          };
          
          outletMarker.setPosition(clickedLocation);
          setTempLocation(newLocation);
          
          // Auto-confirm the new location
          setTimeout(() => {
            onConfirm(newLocation);
          }, 300); // Small delay for visual feedback
        });

        setMapLoaded(true);
      });
    } catch (error) {
      console.error('Error initializing map:', error);
      // Only show error for critical issues, not for API loading problems
      if (error instanceof Error && error.message.includes('ReferrerNotAllowedMapError')) {
        alert(`Google Maps API key restrictions prevent loading. Current URL: ${window.location.href}\n\nPlease add this URL to your API key restrictions in Google Cloud Console.`);
      }
    }
  };

  const handleConfirm = () => {
    if (tempLocation.lat !== 0 && tempLocation.lng !== 0) {
      onConfirm(tempLocation);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
            <Map className="w-5 h-5 text-blue-600" />
            <span>Adjust Location</span>
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        <div className="flex-1 relative">
          <div ref={mapRef} className="w-full h-96 rounded-lg" style={{ minHeight: '400px' }} />
          
          {!mapLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading map...</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t bg-gray-50">
          <div className="mb-4">
            <div className="flex items-center justify-center mb-2">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <MapPin className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 text-center mb-2">
              🎯 Drag the marker or tap the map to adjust location
            </p>
            <p className="text-xs text-blue-600 text-center font-medium">
              ✨ Location will be updated automatically when you release the marker
            </p>
            {tempLocation.lat !== 0 && tempLocation.lng !== 0 && (
              <p className="text-xs text-gray-500 text-center mt-2">
                {tempLocation.lat.toFixed(6)}, {tempLocation.lng.toFixed(6)}
              </p>
            )}
          </div>
          
          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="px-8 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapAdjustmentModal;
